import { CLIENT_OPERATING_CONTEXT_HEADER } from "@solomon-apps/shared";

import { orgApiFetch, orgApiFetchVoid } from "./orgApiFetch";

export type ClientApiKeyStatus = "active" | "rotating" | "expired" | "revoked";

export interface ClientApiKeyMetadata {
    id: string;
    label: string;
    environment: string;
    issuerClientUuid: string;
    keyPrefix: string;
    permissions: string[];
    status: ClientApiKeyStatus;
    createdByUuid: string;
    createdAt: string;
    expiresAt: string | null;
    lastUsedAt: string | null;
    revokedAt: string | null;
    rotationEndsAt: string | null;
    replacesKeyId: string | null;
}

export interface CreateClientApiKeyInput {
    label: string;
    expiresAt?: string | null;
}

export interface CreateClientApiKeyResult {
    key: ClientApiKeyMetadata;
    secret: string;
}

export interface RotateClientApiKeyInput {
    expiresAt?: string | null;
}

export interface RotateClientApiKeyResult extends CreateClientApiKeyResult {
    previousKey: ClientApiKeyMetadata;
}

export async function listClientApiKeys(contextUuid: string): Promise<ClientApiKeyMetadata[]> {
    const response = await orgApiFetch<{ data: ClientApiKeyMetadata[] }>("/api/org/client/api-keys", {
        headers: { [CLIENT_OPERATING_CONTEXT_HEADER]: contextUuid }
    });
    return response.data;
}

export function createClientApiKey({
    contextUuid,
    input
}: {
    contextUuid: string;
    input: CreateClientApiKeyInput;
}): Promise<CreateClientApiKeyResult> {
    return orgApiFetch<CreateClientApiKeyResult>("/api/org/client/api-keys", {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            [CLIENT_OPERATING_CONTEXT_HEADER]: contextUuid
        },
        body: JSON.stringify(input)
    });
}

export function rotateClientApiKey({
    contextUuid,
    id,
    input
}: {
    contextUuid: string;
    id: string;
    input: RotateClientApiKeyInput;
}): Promise<RotateClientApiKeyResult> {
    return orgApiFetch<RotateClientApiKeyResult>(`/api/org/client/api-keys/${encodeURIComponent(id)}/rotate`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            [CLIENT_OPERATING_CONTEXT_HEADER]: contextUuid
        },
        body: JSON.stringify(input)
    });
}

export function revokeClientApiKey({ contextUuid, id }: { contextUuid: string; id: string }): Promise<void> {
    return orgApiFetchVoid(`/api/org/client/api-keys/${encodeURIComponent(id)}`, {
        method: "DELETE",
        headers: { [CLIENT_OPERATING_CONTEXT_HEADER]: contextUuid }
    });
}
